import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import {
  getInvitationByToken,
  acceptInvitation,
  type InvitationPublicDetails,
} from '../utils/invitationApi';
import { en } from '../translations/en';

type InvitationState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'expired' }
  | { kind: 'accepted' }
  | { kind: 'revoked' }
  | { kind: 'emailMismatch' }
  | { kind: 'ready'; invitation: InvitationPublicDetails };

const PERMISSION_LABELS: Record<string, string> = {
  view: en.invitation.permissions.view,
  full: en.invitation.permissions.full,
};

const PERMISSION_COLORS: Record<string, 'default' | 'primary' | 'success'> = {
  view: 'default',
  full: 'success',
};

export const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, signOut } = useAuth();
  const { refreshDelegations } = useActiveCatalog();

  const token = searchParams.get('token');
  const [state, setState] = useState<InvitationState>({ kind: 'loading' });
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: en.invitation.errors.noToken });
      return;
    }

    let cancelled = false;

    const fetchInvitation = async () => {
      try {
        const data = await getInvitationByToken(token);

        if (cancelled) return;

        switch (data.status) {
          case 'expired':
            setState({ kind: 'expired' });
            break;
          case 'accepted':
            setState({ kind: 'accepted' });
            break;
          case 'revoked':
            setState({ kind: 'revoked' });
            break;
          default:
            // If user is not authenticated, skip the invitation card and
            // go straight to signup with the invitation context (#939)
            if (!isAuthenticated) {
              sessionStorage.setItem('pendingInvitationToken', token);
              navigate('/signup', {
                replace: true,
                state: {
                  selectedTier: 'delegate',
                  invitationContext: {
                    collectionName: data.collectionName,
                    delegatorName: data.delegatorName,
                  },
                },
              });
              return;
            }
            setState({ kind: 'ready', invitation: data });
        }
      } catch {
        if (!cancelled) {
          setState({ kind: 'error', message: en.invitation.errors.fetchFailed });
        }
      }
    };

    fetchInvitation();

    return () => {
      cancelled = true;
    };
  }, [token, isAuthenticated, navigate]);

  const handleAccept = useCallback(async () => {
    if (!token) return;

    setAccepting(true);
    setAcceptError(null);
    try {
      await acceptInvitation(token);
      sessionStorage.removeItem('pendingInvitationToken');
      // Pull the just-granted delegation into ActiveCatalogContext BEFORE
      // redirecting. The context only fetches delegations on login / role
      // change, so without this refresh /my-objects would render from a stale
      // list and the new catalog would be missing from the switcher until a
      // full reload. (The removed DelegateHome page refetched on mount itself.)
      await refreshDelegations();
      // Land on the unified Catalog view; ActiveCatalogContext auto-selects the
      // newly delegated catalog and the Catalog dropdown handles switching.
      navigate('/my-objects', { replace: true });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403 || status === 400) {
        setState({ kind: 'emailMismatch' });
      } else {
        setAcceptError(en.invitation.errors.acceptFailed);
      }
    } finally {
      setAccepting(false);
    }
  }, [token, navigate, refreshDelegations]);

  const handleSignIn = useCallback(() => {
    if (token) {
      sessionStorage.setItem('pendingInvitationToken', token);
    }
    navigate('/auth', {
      state: { from: { pathname: '/invitation/accept', search: `?token=${token}` } },
    });
  }, [token, navigate]);

  const handleCreateAccount = useCallback(() => {
    if (token) {
      sessionStorage.setItem('pendingInvitationToken', token);
    }
    const invitation = state.kind === 'ready' ? state.invitation : null;
    navigate('/signup', {
      state: {
        selectedTier: 'delegate',
        invitationContext: invitation
          ? { collectionName: invitation.collectionName, delegatorName: invitation.delegatorName }
          : undefined,
      },
    });
  }, [token, state, navigate]);

  const handleSignOutAndRetry = useCallback(() => {
    signOut();
    if (token) {
      sessionStorage.setItem('pendingInvitationToken', token);
    }
    navigate('/auth', {
      state: { from: { pathname: '/invitation/accept', search: `?token=${token}` } },
    });
  }, [signOut, token, navigate]);

  // Render loading
  if (state.kind === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography>{en.invitation.loading}</Typography>
        </Stack>
      </Box>
    );
  }

  // Render error states
  if (state.kind === 'error') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{state.message}</Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          {en.invitation.actions.goHome}
        </Button>
      </Box>
    );
  }

  if (state.kind === 'expired') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>{en.invitation.errors.expired}</Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          {en.invitation.actions.goHome}
        </Button>
      </Box>
    );
  }

  if (state.kind === 'accepted') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>{en.invitation.errors.accepted}</Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          {en.invitation.actions.goHome}
        </Button>
      </Box>
    );
  }

  if (state.kind === 'revoked') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{en.invitation.errors.revoked}</Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          {en.invitation.actions.goHome}
        </Button>
      </Box>
    );
  }

  if (state.kind === 'emailMismatch') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {en.invitation.errors.emailMismatch}
        </Alert>
        <Button variant="contained" onClick={handleSignOutAndRetry}>
          {en.invitation.actions.signOutTryAnother}
        </Button>
      </Box>
    );
  }

  // Render invitation card (state.kind === 'ready')
  const { invitation } = state;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, px: 2 }}>
      <Typography variant="h4" align="center" gutterBottom>
        {en.invitation.title}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {en.invitation.card.from}
              </Typography>
              <Typography variant="h6">{invitation.delegatorName}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary">
                {en.invitation.card.collection}
              </Typography>
              <Typography variant="h6">{invitation.collectionName}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary">
                {en.invitation.card.accessLevel}
              </Typography>
              <Chip
                label={PERMISSION_LABELS[invitation.permissions] || invitation.permissions}
                color={PERMISSION_COLORS[invitation.permissions] || 'default'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary">
                {en.invitation.card.expires}
              </Typography>
              <Typography>
                {new Date(invitation.expiresAt).toLocaleDateString()}
              </Typography>
            </Box>

            {invitation.message && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {en.invitation.card.message}
                  </Typography>
                  <Typography sx={{ fontStyle: 'italic', mt: 0.5 }}>
                    &ldquo;{invitation.message}&rdquo;
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Accept error alert */}
      {acceptError && (
        <Alert severity="error" sx={{ mt: 2 }}>{acceptError}</Alert>
      )}

      {/* Action buttons */}
      <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
        {isAuthenticated ? (
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? en.invitation.actions.accepting : en.invitation.actions.accept}
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleCreateAccount}
            >
              {en.invitation.actions.createAccount}
            </Button>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                cursor: 'pointer',
                '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                mt: 1,
              }}
              onClick={handleSignIn}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSignIn(); }}
            >
              {en.invitation.actions.signInLink}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default AcceptInvitation;
